import React from 'react';
import {
  BarChart as RechartsBarChart,
  Bar,
  LineChart as RechartsLineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  ChartContainer as StyledChartContainer,
  ChartTitle,
  ChartSubtitle,
  ChartSource,
} from '@fuww/library/src/Charts/Styled';

interface ChartBaseProperties {
  title?: string;
  subtitle?: string;
  source?: string;
  data: Record<string, unknown>[];
  height?: number;
  width?: string;
  dataKey: string;
  valuePrefix?: string;
  valueSuffix?: string;
  valueLabel?: string;
}

interface BarChartProperties extends ChartBaseProperties {
  xAxisKey: string;
  color?: string;
  valueScale?: number;
}

interface LineChartProperties extends ChartBaseProperties {
  xAxisKey: string;
  color?: string;
  yDomain?: [number, number];
}

interface PieChartProperties extends ChartBaseProperties {
  nameKey: string;
  colors?: string[];
}

interface ChartContainerProperties {
  title?: string;
  subtitle?: string;
  source?: string;
  children: React.ReactNode;
}

const ChartContainer = ({
  title,
  subtitle,
  source,
  children,
}: ChartContainerProperties) => (
  <StyledChartContainer>
    {title && <ChartTitle>{title}</ChartTitle>}
    {subtitle && <ChartSubtitle>{subtitle}</ChartSubtitle>}
    {children}
    {source && (
      <ChartSource>
        Source:
        {' '}
        {source}
      </ChartSource>
    )}
  </StyledChartContainer>
);

export const BarChart = ({
  title,
  subtitle,
  source,
  data,
  dataKey,
  xAxisKey,
  valuePrefix,
  valueSuffix,
  valueLabel,
  color,
  valueScale,
  height,
  width,
}: BarChartProperties) => {
  const scale = valueScale ?? 1;

  return (
    <ChartContainer title={title} subtitle={subtitle} source={source}>
      <ResponsiveContainer width={width ?? '100%'} height={height ?? 400}>
        <RechartsBarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xAxisKey} />
          <YAxis
            tickFormatter={(value) => (
              `${valuePrefix ?? ''}${scale === 1
                ? value : (value / scale).toFixed(1)}${
                valueSuffix ?? ''
              }`
            )}
          />
          <Tooltip />
          <Legend />
          <Bar
            dataKey={dataKey}
            name={valueLabel ?? 'Value'}
            fill={color ?? '#8884d8'}
          />
        </RechartsBarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

export const LineChart = ({
  title,
  subtitle,
  source,
  data,
  dataKey,
  xAxisKey,
  valuePrefix,
  valueSuffix,
  valueLabel,
  color,
  yDomain,
  height,
  width,
}: LineChartProperties) => {
  const valuePrefixValue = valuePrefix ?? '';
  const valueSuffixValue = valueSuffix ?? '';
  const valueLabelValue = valueLabel ?? 'Value';

  return (
    <ChartContainer title={title} subtitle={subtitle} source={source}>
      <ResponsiveContainer width={width ?? '100%'} height={height ?? 400}>
        <RechartsLineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xAxisKey} />
          <YAxis
            domain={yDomain}
            tickFormatter={(value) => (
              `${valuePrefixValue}${value}${valueSuffixValue}`
            )}
          />
          <Tooltip
            formatter={(value) => [
              `${valuePrefixValue}${value}${valueSuffixValue}`,
              valueLabelValue,
            ]}
          />
          <Legend />
          <Line
            type="monotone"
            name={valueLabelValue}
            dataKey={dataKey}
            stroke={color ?? '#82ca9d'}
            strokeWidth={2}
            dot={{ r: 6 }}
            activeDot={{ r: 8 }}
          />
        </RechartsLineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

export const PieChart = ({
  title,
  subtitle,
  source,
  data,
  dataKey,
  nameKey,
  valuePrefix,
  valueSuffix,
  valueLabel,
  colors,
  height,
  width,
}: PieChartProperties) => {
  const valuePrefixValue = valuePrefix ?? '';
  const valueSuffixValue = valueSuffix ?? '';
  const valueLabelValue = valueLabel ?? 'Value';
  const colorsValue = colors ?? [
    '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8',
  ];

  return (
    <ChartContainer title={title} subtitle={subtitle} source={source}>
      <ResponsiveContainer width={width ?? '100%'} height={height ?? 400}>
        <RechartsPieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine
            outerRadius={150}
            fill="#8884d8"
            dataKey={dataKey}
            nameKey={nameKey}
            label={({ name, value }) => (
              `${name}: ${valuePrefixValue}${value}${valueSuffixValue}`
            )}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${entry[nameKey]}`}
                fill={colorsValue[index % colorsValue.length]}
              />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) => [
              `${valuePrefixValue}${value}${valueSuffixValue}`,
              valueLabelValue,
            ]}
          />
          <Legend />
        </RechartsPieChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

export { ChartGrid } from '@fuww/library/src/Charts/Styled';
